import { PrinterOutlined } from "@ant-design/icons";
import { Button, DatePicker, Form, Select, Table, message } from "antd";
import axios from "axios";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import ReactToPrint from "react-to-print";
import { API, Headers } from "../../Config";
import PageHeader from "../../Routes/PageHeader";
const { RangePicker } = DatePicker;

const StatementPage = () => {
  const [loading, setLoading] = useState(false);
  const componentRef = useRef();
  const [form] = Form.useForm();
  const [filteredDataSource, setFilteredDataSource] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [account_no, setAccountNo] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    pageSizeOptions: [10, 20, 30, 40, 50],
    showSizeChanger: true,
  });
  const [disablePagination, setDisablePagination] = useState(false); // New state to control pagination

  useEffect(() => {
    AccountsDropdown();
  }, []);

  const handleSearch = () => {
    form.validateFields().then((values) => {
      const { dateRange, account_no } = values;
      fetchFilteredData(dateRange, account_no);
    });
  };

  const fetchFilteredData = async (dateRange, account) => {
    const [startDate, endDate] = dateRange || [];
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : null;
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : null;
    setDateRange(dateRange || []);
    setAccountNo(account);
    setLoading(true);

    if (!account) {
      message.error("Please select account");
      setLoading(false);
      return;
    }

    try {
      const response = await axios({
        method: "post",
        url: API.BASR_URL + "ledgerTransactions/stmt",
        headers: Headers(),
        data: {
          date_from: formattedStartDate,
          date_to: formattedEndDate,
          account_no: account,
          order: "id DESC",
        },
      });

      setLoading(false);
      if ([200, 201].includes(response.status)) {
        message.success("Success");
        const sortedData = response.data;
        setFilteredDataSource(sortedData);
        setPagination((prev) => ({
          ...prev,
          current: 1,
          total: sortedData.length,
        }));
      }
    } catch (error) {
      setLoading(false);
      message.error("Oops. Something went wrong");
      console.error("Fetch error:", error);
    }
  };

  const AccountsDropdown = async () => {
    try {
      const res = await axios.get(
        API.BASR_URL + API.CHART_OF_ACCOUNTS_LIST + `/child_accounts`,
        { headers: Headers() }
      );
      if (res.status === 200) {
        setAccounts(res.data);
      }
    } catch (error) {
      message.error("Customers dropdown failed");
      console.error("Accounts dropdown error:", error);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    });
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: "20px",
      render: (text, record, index) =>
        disablePagination
          ? index + 1 // No pagination offset when printing
          : index + 1 + (pagination.current - 1) * pagination.pageSize,
    },
    {
      title: "Date",
      dataIndex: "trans_date",
      key: "trans_date",
      render: (text) => (text ? moment(text).format("DD-MM-YYYY") : "-"),
    },
    {
      title: "Time",
      dataIndex: "trans_date",
      key: "trans_time",
      render: (text) => (text ? moment(text).format("hh:mm:ss A") : "-"),
    },
    {
      title: "Reference No",
      dataIndex: "reference_no",
      key: "reference_no",
      render: (text) => text || "-",
    },
    {
      title: "Description",
      dataIndex: "note",
      key: "note",
      render: (text) => text || "-",
    },
    {
      title: "Transaction Type",
      dataIndex: "trans_type",
      key: "trans_type",
      render: (text) => text || "-",
    },
    {
      title: "Amount (Dr)",
      dataIndex: "amount_dr",
      key: "amount_dr",
      render: (text, record) =>
        record.dr_or_cr === "Dr"
          ? Math.abs(Number(record.amount)).toFixed(2)
          : "0.00",
    },
    {
      title: "Amount (Cr)",
      dataIndex: "amount_cr",
      key: "amount_cr",
      render: (text, record) =>
        record.dr_or_cr === "Cr"
          ? Math.abs(Number(record.amount)).toFixed(2)
          : "0.00",
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (text) => Number(text).toFixed(2),
    },
  ];

  const renderSummary = () => {
    const totalDrAmount = filteredDataSource.reduce(
      (acc, item) => (item.dr_or_cr === "Dr" ? acc + Number(item.amount) : acc),
      0
    );
    const totalCrAmount = filteredDataSource.reduce(
      (acc, item) => (item.dr_or_cr === "Cr" ? acc + Number(item.amount) : acc),
      0
    );
    const balance = (totalDrAmount - totalCrAmount).toFixed(2);
    const Dr_or_Cr = balance >= 0 ? "Dr" : "Cr";

    return (
      <Table.Summary.Row className="my-summary-row">
        <Table.Summary.Cell className="cell" />
        <Table.Summary.Cell className="cell" />
        <Table.Summary.Cell className="cell" />
        <Table.Summary.Cell className="cell" />
        <Table.Summary.Cell className="cell" />
        <Table.Summary.Cell className="cell">
          <b>Total</b>
        </Table.Summary.Cell>
        <Table.Summary.Cell className="cell">
          <b style={{ paddingLeft: "9px" }}>
            {Math.abs(totalDrAmount).toFixed(2)}
          </b>
        </Table.Summary.Cell>
        <Table.Summary.Cell className="cell">
          <b style={{ paddingLeft: "9px" }}>
            {Math.abs(totalCrAmount).toFixed(2)}
          </b>
        </Table.Summary.Cell>
        <Table.Summary.Cell className="cell">
          <b style={{ paddingLeft: "9px" }}>
            {Math.abs(Number(balance)).toFixed(2)} {Dr_or_Cr}
          </b>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    );
  };

  return (
    <div>
      <PageHeader
        PageName={"Statement Report"}
        breadScrum={"Admin / Statement Report"}
      />
      <Form
        form={form}
        layout="inline"
        style={{
          marginBottom: 16,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Form.Item name="dateRange">
          <RangePicker size="small" style={{ width: 240 }} />
        </Form.Item>
        <Form.Item name="account_no">
          <Select
            size="small"
            placeholder="Select account"
            showSearch
            allowClear
            style={{ borderRadius: "0", width: "400px" }}
            filterOption={(input, option) => {
              const accountNo = String(option.value).toLowerCase();
              const accountName = option.children.toString().toLowerCase();
              return (
                accountNo.includes(input.toLowerCase()) ||
                accountName.includes(input.toLowerCase())
              );
            }}
          >
            {accounts.map((item) => (
              <Select.Option key={item.account_no} value={item.account_no}>
                {item.account_no} - {item.account_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button
            size="small"
            type="primary"
            onClick={handleSearch}
            loading={loading}
          >
            Search
          </Button>
        </Form.Item>
        <Form.Item>
          <ReactToPrint
            trigger={() => (
              <Button
                size="small"
                type="primary"
                icon={<PrinterOutlined />}
                style={{ marginLeft: 8 }}
              >
                Print Statement
              </Button>
            )}
            content={() => componentRef.current}
            onBeforeGetContent={() => {
              setDisablePagination(true); // Disable pagination for printing
              return Promise.resolve();
            }}
            onAfterPrint={() => {
              setDisablePagination(false); // Re-enable pagination after printing
            }}
          />
        </Form.Item>
      </Form>
      <div ref={componentRef} style={{ margin: "20px 20px" }}>
        <div style={{ marginBottom: "10px" }}>
          <b>Date Range:</b>{" "}
          {dateRange[0] ? moment(dateRange[0]).format("DD-MM-YYYY") : ""} to{" "}
          {dateRange[1] ? moment(dateRange[1]).format("DD-MM-YYYY") : ""}
        </div>
        <div style={{ marginBottom: "10px" }}>
          <b>Account:</b>{" "}
          {accounts.find((item) => item.account_no === account_no)
            ?.account_name || ""}
        </div>

        <Table
          size="small"
          columns={columns}
          dataSource={filteredDataSource}
          pagination={disablePagination ? false : pagination} // Disable pagination when printing
          onChange={handleTableChange}
          summary={renderSummary}
          rowKey="id"
        />
      </div>
    </div>
  );
};

export default StatementPage;
